import { useState, useEffect, useRef, ReactNode } from 'react';

import urlParser from 'bloko/common/urlParser';

import submitPostData from 'Utils/SubmitPostData';
import { usePreparedVacancy } from 'src/components/VacancyCreate/hooks/usePreparedVacancy';
import { useSelector } from 'src/hooks/useSelector';
import { VacancyCreateFormValues } from 'src/models/vacancyCreate/vacancyCreate.types';

export const IFRAME_NAME = 'previewFrame';

const getVacancyPreviewUrl = (parsedUrl: ReturnType<typeof urlParser>, vacancyId: number) =>
    `/vacancy/preview?${urlParser.stringify({
        ...parsedUrl.params,
        id: vacancyId,
        modPreview: 'true',
    })}`;

const getDraftPreviewUrl = (parsedUrl: ReturnType<typeof urlParser>, draftId: number | null, showHidden?: boolean) =>
    `/vacancy/preview_draft?${urlParser.stringify({
        ...parsedUrl.params,
        draftId,
        doPreview: 'true',
        showHidden: showHidden ? 'true' : null,
    })}`;

const getFormPreviewUrl = (parsedUrl: ReturnType<typeof urlParser>, toolsVisible: boolean) =>
    `/vacancy/preview?${urlParser.stringify({ ...parsedUrl.params, doPreview: 'true', toolsVisible: toolsVisible ? 'true' : null })}`;

const GAP = 5;

interface VacancyPreviewProps {
    vacancyId?: number | null;
    draftId?: number | null;
    formValues?: VacancyCreateFormValues;
    visible: boolean;
    toolsVisible?: boolean;
    IFrameWrapper: React.ExoticComponent<{ children?: ReactNode }> | React.FC<{ children?: ReactNode }>;
    showHidden?: boolean;
}

const VacancyPreview: React.FC<VacancyPreviewProps> = ({
    vacancyId,
    draftId,
    formValues,
    visible,
    toolsVisible = false,
    IFrameWrapper,
    showHidden,
}) => {
    const { pathname, search } = useSelector(({ router }) => router.location);
    const [spinnerVisible, setSpinnerVisible] = useState(true);
    const prepareVacancy = usePreparedVacancy(true);

    const iFrameRef = useRef<HTMLIFrameElement | null>(null);
    const resizeObserverRef = useRef<ResizeObserver>();

    const currentUrl = `${pathname}${search}`;

    useEffect(() => {
        return () => {
            resizeObserverRef.current?.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!visible || !vacancyId) {
            return;
        }

        submitPostData({
            url: getVacancyPreviewUrl(urlParser(currentUrl), vacancyId),
            target: IFRAME_NAME,
            data: {},
        });
    }, [vacancyId, visible, currentUrl]);

    useEffect(() => {
        if (!visible || !draftId) {
            return;
        }

        submitPostData({
            url: getDraftPreviewUrl(urlParser(currentUrl), draftId, showHidden),
            target: IFRAME_NAME,
            data: {},
        });
    }, [visible, draftId, currentUrl, showHidden]);

    useEffect(() => {
        if (!visible || !formValues) {
            return;
        }

        const preparedVacancy = prepareVacancy(formValues);
        const vacancyJSONBody = JSON.stringify(preparedVacancy);

        submitPostData({
            url: getFormPreviewUrl(urlParser(currentUrl), toolsVisible),
            target: IFRAME_NAME,
            data: { vacancyJSONBody },
        });
    }, [visible, formValues, prepareVacancy, currentUrl, toolsVisible]);

    const hideSpinner = () => {
        setSpinnerVisible(false);
    };

    const handleIFrameLoad = () => {
        if (iFrameRef?.current?.contentWindow) {
            resizeObserverRef.current = new ResizeObserver((entries) => {
                entries.forEach((entry) => {
                    if (iFrameRef.current) {
                        let iFrameHeight = entry.target.scrollHeight;
                        iFrameHeight += GAP;

                        iFrameRef.current.style.height = `${iFrameHeight}px`;
                    }
                });
            });
            resizeObserverRef.current.observe(iFrameRef.current.contentWindow.document.body);
        }

        hideSpinner();
    };

    return (
        <div className="vacancy-preview-popup">
            {spinnerVisible && <span className="prosper-spinner prosper-spinner_big prosper-spinner_centered" />}
            <IFrameWrapper>
                <iframe
                    ref={iFrameRef}
                    name={IFRAME_NAME}
                    className="vacancy-preview-iframe"
                    data-qa="vacancycreate-preview-iframe"
                    scrolling="no"
                    onLoad={handleIFrameLoad}
                />
            </IFrameWrapper>
        </div>
    );
};

export default VacancyPreview;
